import React, {useEffect, useState, useRef} from "react";
import {format} from "date-fns";
import {Card, Container} from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import filterFactory, {textFilter} from "react-bootstrap-table2-filter";
import axios from "../../util/Axios/AxiosInstance";
import AOS from "aos";
import "aos/dist/aos.css";
import Skeleton from "react-loading-skeleton";
import eyeImage from "../../../images/eye.png";
import TableActionIcon from "../../util/TableActionIcon";
import "../ReportsSection/ReportSection.css";
import {PAYOUT_SOURCE_FACIAL} from "./PayoutSourceToggle";
import PayoutToolbar from "./PayoutToolbar";
import {exportPayoutTableToCsv} from "./payoutExportUtils";

const createSkeletonRows = () =>
    Array.from({length: 8}, () => ({
        labourName: <Skeleton/>,
        totalMandays: <Skeleton/>,
        workHrs: <Skeleton/>,
        incentive: <Skeleton/>,
        serviceCharges: <Skeleton/>,
        totalBasic: <Skeleton/>,
        totalEmployerPf: <Skeleton/>,
        totalEmployerEsi: <Skeleton/>,
        grossAmount: <Skeleton/>,
        gstAmount: <Skeleton/>,
        grandTotal: <Skeleton/>,
    }));

const formatAmount = (value) => {
    if (value == null || value === "") {
        return "";
    }
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(2) : "";
};

const amountFormatter = (cell, row) =>
    row?.wagesAvailable ? formatAmount(cell) : "";

function LabourPay({vendorId, rangeValue, payoutSource: initialSource, HandleEventGetLabourId}) {
    const dateToday = new Date();
    const firstDay = new Date(dateToday.getFullYear(), dateToday.getMonth(), 1);
    const lastDay = new Date(dateToday.getFullYear(), dateToday.getMonth() + 1, 0);

    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState(createSkeletonRows());
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [open, setOpen] = useState(false);
    const [payoutSource, setPayoutSource] = useState(initialSource || PAYOUT_SOURCE_FACIAL);
    const pickerRef = useRef(null);
    const [range, setRange] = useState(
        rangeValue && rangeValue[0]
            ? rangeValue
            : [
                  {
                      startDate: firstDay,
                      endDate: lastDay,
                      key: "selection",
                  },
              ]
    );

    const options = {
        sizePerPage: pageSize,
        onSizePerPageChange: (sizePerPage) => setPageSize(sizePerPage),
        onPageChange: (page) => setPageNumber(page),
    };

    const getLabourPayout = async (selectedRange, source) => {
        const branchId = localStorage.getItem("branchId");
        const startDate = format(selectedRange[0].startDate, "yyyy-MM-dd");
        const endDate = format(selectedRange[0].endDate, "yyyy-MM-dd");
        setLoading(true);
        setTableData(createSkeletonRows());
        try {
            const response = await axios.get(
                `payout/labours/${vendorId}/${startDate}/${endDate}`,
                {params: {branchId, source: source || payoutSource}}
            );
            setTableData(Array.isArray(response.data) ? response.data : []);
        } catch (e) {
            console.error(e);
            setTableData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        AOS.init();
    }, []);

    useEffect(() => {
        if (initialSource) {
            setPayoutSource(initialSource);
        }
    }, [initialSource]);

    useEffect(() => {
        if (open) {
            return;
        }
        getLabourPayout(range, payoutSource);
    }, [open, range, vendorId, payoutSource]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    const columns = [
        {
            dataField: "labourId",
            text: "S No.",
            formatter: (cell, row, rowIndex) => (pageNumber - 1) * pageSize + (rowIndex + 1),
            headerStyle: () => ({width: "60px", textAlign: "center"}),
        },
        {
            dataField: "labourCode",
            text: "Emp Code",
            sort: true,
            filter: textFilter(),
            title: (cell) => `${cell ?? ""}`,
        },
        {
            dataField: "labourName",
            text: "Name",
            sort: true,
            filter: textFilter(),
            title: (cell) => `${cell ?? ""}`,
        },
        {
            dataField: "totalMandays",
            text: "Mandays",
            sort: true,
            formatter: (cell) => formatAmount(cell),
            title: (cell) => `${cell ?? ""}`,
        },
        {
            dataField: "workHrs",
            text: "Total Hrs worked",
            sort: true,
            formatter: (cell) => formatAmount(cell),
            title: (cell) => `${cell ?? ""}`,
        },
        {
            dataField: "incentive",
            text: "Incentive",
            sort: true,
            formatter: amountFormatter,
            title: (cell, row) => (row?.wagesAvailable ? `${cell ?? ""}` : ""),
        },
        {
            dataField: "serviceCharges",
            text: "Service Charges",
            sort: true,
            formatter: amountFormatter,
            title: (cell, row) => (row?.wagesAvailable ? `${cell ?? ""}` : ""),
        },
        {
            dataField: "totalBasic",
            text: "Total Basic Amount",
            sort: true,
            formatter: amountFormatter,
            title: (cell, row) => (row?.wagesAvailable ? `${cell ?? ""}` : ""),
        },
        {
            dataField: "totalEmployerPf",
            text: "Total Employer PF",
            sort: true,
            formatter: amountFormatter,
            title: (cell, row) => (row?.wagesAvailable ? `${cell ?? ""}` : ""),
        },
        {
            dataField: "totalEmployerEsi",
            text: "Total Employer ESI",
            sort: true,
            formatter: amountFormatter,
            title: (cell, row) => (row?.wagesAvailable ? `${cell ?? ""}` : ""),
        },
        {
            dataField: "grossAmount",
            text: "Gross Total",
            sort: true,
            formatter: amountFormatter,
            title: (cell, row) => (row?.wagesAvailable ? `${cell ?? ""}` : ""),
        },
        {
            dataField: "gstAmount",
            text: "Total GST",
            sort: true,
            formatter: amountFormatter,
            title: (cell, row) => (row?.wagesAvailable ? `${cell ?? ""}` : ""),
        },
        {
            dataField: "grandTotal",
            text: "Grand Total",
            sort: true,
            formatter: amountFormatter,
            title: (cell, row) => (row?.wagesAvailable ? `${cell ?? ""}` : ""),
        },
        {
            dataField: "Actions",
            text: "View",
            isDummyField: true,
            headerStyle: () => ({width: "70px", textAlign: "center"}),
            formatter: (cellContent, row) => (
                <TableActionIcon
                    src={eyeImage}
                    title="View daily payout"
                    size={28}
                    disabled={!row?.labourId}
                    onClick={() => {
                        if (row?.labourId) {
                            HandleEventGetLabourId(103, row.labourId, range, payoutSource);
                        }
                    }}
                />
            ),
        },
    ];

    const exportDateLabel = `${format(range[0].startDate, "dd-MM-yyyy")}_${format(
        range[0].endDate,
        "dd-MM-yyyy"
    )}`;

    const handleExport = () => {
        exportPayoutTableToCsv({
            columns,
            data: tableData,
            fileName: "LabourPayout",
            dateLabel: exportDateLabel,
        });
    };

    return (
        <Card className="cardStyl">
            <Container fluid className="px-0">
                <div className="mt-2">
                    <h5 className="priColor mb-0">Vendor Payout — Labour Wise</h5>
                </div>
                <PayoutToolbar
                    source={payoutSource}
                    disabled={loading}
                    onSourceChange={(next) => {
                        setPageNumber(1);
                        setPayoutSource(next);
                    }}
                    range={range}
                    setRange={setRange}
                    open={open}
                    setOpen={setOpen}
                    pickerRef={pickerRef}
                    onExport={handleExport}
                    exportDisabled={loading || !tableData.length}
                />
                <div data-aos="zoom-in-right" data-aos-duration="550">
                    <BootstrapTable
                        bootstrap4
                        keyField="labourId"
                        data={tableData}
                        columns={columns}
                        wrapperClasses="table-responsive"
                        pagination={paginationFactory(options)}
                        filter={filterFactory()}
                        noDataIndication={loading ? "Loading..." : "No labour payout data available"}
                    />
                </div>
            </Container>
        </Card>
    );
}

export default LabourPay;
